import { useEffect, useMemo, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  TASK_STATUSES,
  type DocumentPointer,
  type DocumentVersion,
  type PrdContent,
  type Task,
  type TddContent,
} from '@allemnii-pulse/shared';
import { useCollection, useDocument } from '../../lib/firestore.js';
import { useProject } from '../projects/ProjectProvider.js';
import { UserName } from '../../components/UserName.js';
import { Button } from '../../components/ui/Button.js';
import { scheduleTasks, scheduledDates } from '../../lib/schedule.js';

type PrintDocType = 'prd' | 'tdd' | 'tasks' | 'timeline';

const DOC_LABEL: Record<PrintDocType, string> = {
  prd: 'PRD',
  tdd: 'TDD',
  tasks: 'Tasks',
  timeline: 'Timeline',
};

const STATUS_LABEL: Record<Task['status'], string> = {
  backlog: 'Backlog',
  in_progress: 'In Progress',
  on_hold: 'On Hold',
  blocked: 'Blocked',
  testing: 'Testing',
  in_review: 'In Review',
  done: 'Done',
  canceled: 'Canceled',
};

/** Forces light theme for as long as this route is mounted — the print output should never
 * depend on the viewer's OS/app theme preference (docs/PLAN.md §8), and this reuses the existing
 * semantic tokens (bg-bg, text-text, ...) rather than a parallel colour set. */
function useForcedLightTheme() {
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute('data-theme');
    root.setAttribute('data-theme', 'light');
    return () => {
      if (previous) root.setAttribute('data-theme', previous);
      else root.removeAttribute('data-theme');
    };
  }, []);
}

function renderMarkdown(value: string): string {
  return DOMPurify.sanitize(marked.parse(value || '', { async: false }));
}

function PrintShell({
  projectName,
  docType,
  versionNumber,
  children,
}: {
  projectName: string;
  docType: PrintDocType;
  versionNumber?: number;
  children: ReactNode;
}) {
  const generatedAt = useMemo(() => new Date().toLocaleString(), []);
  const docLabel = DOC_LABEL[docType] + (versionNumber ? ` — v${versionNumber}` : '');

  return (
    <div className="print-doc mx-auto min-h-screen max-w-[48rem] bg-bg px-10 py-10 text-[13px] leading-relaxed text-text print:px-12">
      <div className="mb-6 flex justify-end print:hidden">
        <Button type="button" variant="default" onClick={() => window.print()}>
          Print / Save as PDF
        </Button>
      </div>

      {/* Repeating running header/footer — position:fixed elements print on every page in
          Chrome/Edge, which is what "the user prints to PDF from the browser" (§8) relies on.
          True "page X of Y" counters need CSS Paged Media margin boxes, which no browser
          implements without a polyfill, so the footer carries identity + timestamp instead. */}
      <div className="fixed inset-x-0 top-0 z-10 hidden items-center justify-between border-b border-border bg-bg px-12 py-2 text-[10px] uppercase tracking-wide text-muted print:flex">
        <span>{projectName}</span>
        <span>{docLabel}</span>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-10 hidden items-center justify-between border-t border-border bg-bg px-12 py-2 text-[10px] text-muted print:flex">
        <span>Generated {generatedAt}</span>
        <span>Allemnii Pulse</span>
      </div>

      {/* The fixed header/footer overlay every page, so every page needs room reserved for them —
          padding on a wrapper would only clear the first page's top and the last page's bottom.
          Browsers repeat <thead>/<tfoot> on each printed page, so empty spacers there do it. */}
      <table role="presentation" className="w-full border-collapse">
        <thead className="hidden print:table-header-group">
          <tr>
            <td className="p-0">
              <div className="h-12" />
            </td>
          </tr>
        </thead>
        <tfoot className="hidden print:table-footer-group">
          <tr>
            <td className="p-0">
              <div className="h-12" />
            </td>
          </tr>
        </tfoot>
        <tbody>
          <tr>
            <td className="p-0 align-top">
              <h1 className="mb-1 text-xl font-semibold">
                {projectName} — {docLabel}
              </h1>
              <div className="mb-8 text-xs text-muted print:hidden">Generated {generatedAt}</div>
              {children}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function PrdPrintBody({ content }: { content: PrdContent }) {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-8 text-sm">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted">Start date</div>
          <div>{content.startDate}</div>
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-muted">End date</div>
          <div>{content.endDate ?? '—'}</div>
        </div>
      </div>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Problem statement</h2>
        <div
          className="doc-prose"
          dangerouslySetInnerHTML={{ __html: renderMarkdown(content.problemStatement) }}
        />
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Target audience</h2>
        <ul className="list-disc pl-5 text-sm">
          {content.targetAudience.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      </section>

      <section>
        <h2 className="mb-2 text-sm font-semibold">Key user stories</h2>
        <ul className="flex flex-col gap-1.5 text-sm">
          {content.userStories.map((s) => (
            <li key={s.id}>
              <span className="font-mono text-xs text-muted">{s.id}</span>
              {s.priority && <span className="text-xs text-muted"> ({s.priority})</span>} — As a{' '}
              {s.role}, I want {s.want}, so that {s.benefit}.
              {s.acceptanceCriteria && s.acceptanceCriteria.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {s.acceptanceCriteria.map((c, ci) => (
                    <li key={ci}>{c}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      </section>

      {content.outOfScope.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold">Out of scope</h2>
          <ul className="list-disc pl-5 text-sm">
            {content.outOfScope.map((o, i) => (
              <li key={i}>{o}</li>
            ))}
          </ul>
        </section>
      )}

      {content.goals && content.goals.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold">Goals and success metrics</h2>
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border text-left text-muted">
                <th className="py-1.5 pr-2 font-medium">Goal</th>
                <th className="py-1.5 pr-2 font-medium">Metric</th>
                <th className="py-1.5 pr-2 font-medium">Target</th>
              </tr>
            </thead>
            <tbody>
              {content.goals.map((g, i) => (
                <tr key={i} className="border-b border-border">
                  <td className="py-1.5 pr-2">{g.goal}</td>
                  <td className="py-1.5 pr-2">{g.metric ?? '—'}</td>
                  <td className="py-1.5 pr-2">{g.target ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <PrdPrintList title="Non-functional requirements and constraints" items={content.nonFunctionalRequirements} />
      <PrdPrintList title="Assumptions" items={content.assumptions} />
      <PrdPrintList title="Dependencies" items={content.dependencies} />
      <PrdPrintList title="Risks" items={content.risks} />
      <PrdPrintList title="Open questions" items={content.openQuestions} />
    </div>
  );
}

/** An optional PRD list section — rendered only when the PRD states it. */
function PrdPrintList({ title, items }: { title: string; items: string[] | undefined }) {
  if (!items?.length) return null;
  return (
    <section>
      <h2 className="mb-2 text-sm font-semibold">{title}</h2>
      <ul className="list-disc pl-5 text-sm">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

function TddPrintBody({ content }: { content: TddContent }) {
  return (
    <div className="flex flex-col gap-8">
      {content.sections.map((section, i) => (
        <section key={section.id} className={i > 0 ? 'print:break-before-page' : undefined}>
          <h2 className="mb-2 text-sm font-semibold">{section.title}</h2>
          <div
            className="doc-prose"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(section.body) }}
          />
        </section>
      ))}
    </div>
  );
}

function TasksPrintBody({ tasks }: { tasks: Task[] }) {
  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const groups = TASK_STATUSES.map((status) => ({
    status,
    tasks: tasks.filter((t) => t.status === status),
  })).filter((g) => g.tasks.length > 0);

  if (tasks.length === 0) return <p className="text-sm text-muted">No tasks yet.</p>;

  return (
    <div className="flex flex-col gap-6">
      {groups.map((g) => (
        <section key={g.status} className="print:break-inside-avoid">
          <h2 className="mb-2 text-sm font-semibold">
            {STATUS_LABEL[g.status]} ({g.tasks.length})
          </h2>
          <table className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-border text-left text-muted">
                <th className="py-1.5 pr-2 font-medium">Title</th>
                <th className="py-1.5 pr-2 font-medium">Priority</th>
                <th className="py-1.5 pr-2 font-medium">Assignee</th>
                <th className="py-1.5 pr-2 font-medium">Depends on</th>
              </tr>
            </thead>
            <tbody>
              {g.tasks.map((t) => (
                <tr key={t.id} className="border-b border-border/60">
                  <td className="py-1.5 pr-2">{t.title}</td>
                  <td className="py-1.5 pr-2 capitalize">{t.priority}</td>
                  <td className="py-1.5 pr-2">
                    {t.assigneeUid ? <UserName uid={t.assigneeUid} showStatus={false} /> : '—'}
                  </td>
                  <td className="py-1.5 pr-2">
                    {t.dependsOn.length > 0
                      ? t.dependsOn.map((id) => byId.get(id)?.title ?? id).join(', ')
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

function TimelinePrintBody({ tasks, startDate }: { tasks: Task[]; startDate: string }) {
  const byId = useMemo(() => new Map(tasks.map((t) => [t.id, t])), [tasks]);
  const schedule = useMemo(() => scheduleTasks(tasks), [tasks]);
  const sorted = useMemo(
    () =>
      [...tasks].sort(
        (a, b) =>
          (schedule.get(a.id)?.startDayOffset ?? 0) - (schedule.get(b.id)?.startDayOffset ?? 0),
      ),
    [tasks, schedule],
  );

  if (tasks.length === 0) return <p className="text-sm text-muted">No tasks yet.</p>;

  return (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr className="border-b border-border text-left text-muted">
          <th className="py-1.5 pr-2 font-medium">Title</th>
          <th className="py-1.5 pr-2 font-medium">Status</th>
          <th className="py-1.5 pr-2 font-medium">Start</th>
          <th className="py-1.5 pr-2 font-medium">End</th>
          <th className="py-1.5 pr-2 font-medium">Depends on</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((t) => {
          const s = schedule.get(t.id);
          const { start, end } = s ? scheduledDates(startDate, s) : { start: null, end: null };
          return (
            <tr key={t.id} className="border-b border-border/60">
              <td className="py-1.5 pr-2">{t.title}</td>
              <td className="py-1.5 pr-2">{STATUS_LABEL[t.status]}</td>
              <td className="py-1.5 pr-2">{start?.toLocaleDateString() ?? '—'}</td>
              <td className="py-1.5 pr-2">{end?.toLocaleDateString() ?? '—'}</td>
              <td className="py-1.5 pr-2">
                {t.dependsOn.length > 0
                  ? t.dependsOn.map((id) => byId.get(id)?.title ?? id).join(', ')
                  : '—'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Print-optimised route — docs/PLAN.md §8: `/print/:projectId/:docType`, rendered outside the
 * app shell (no sidebar/nav chrome), forced light, with a repeating header/footer and page
 * breaks between sections. The user prints to PDF from here via the browser's own print dialog;
 * there is no server-side PDF generation (no headless Chromium in the API image). */
export function PrintPage() {
  useForcedLightTheme();
  const { docType } = useParams<{ docType: PrintDocType }>();
  const { projectId, project, loading: projectLoading } = useProject();

  const { data: prdPointer } = useDocument<DocumentPointer>(
    docType === 'prd' ? `projects/${projectId}/documents/prd` : null,
  );
  const { data: prdVersion } = useDocument<DocumentVersion>(
    docType === 'prd' && prdPointer?.currentVersionId
      ? `projects/${projectId}/documents/prd/versions/${prdPointer.currentVersionId}`
      : null,
  );

  const { data: tddPointer } = useDocument<DocumentPointer>(
    docType === 'tdd' ? `projects/${projectId}/documents/tdd` : null,
  );
  const { data: tddVersion } = useDocument<DocumentVersion>(
    docType === 'tdd' && tddPointer?.currentVersionId
      ? `projects/${projectId}/documents/tdd/versions/${tddPointer.currentVersionId}`
      : null,
  );

  const { data: tasks, loading: tasksLoading } = useCollection<Task>(
    docType === 'tasks' || docType === 'timeline' ? `projects/${projectId}/tasks` : null,
  );

  if (!docType || !(docType in DOC_LABEL)) {
    return <div className="p-10 text-sm text-danger">Unknown document type.</div>;
  }
  if (projectLoading || !project) {
    return <div className="p-10 text-sm text-muted">Loading…</div>;
  }

  if (docType === 'prd') {
    if (!prdVersion) {
      return (
        <PrintShell projectName={project.name} docType={docType}>
          <p className="text-sm text-muted">No accepted PRD yet.</p>
        </PrintShell>
      );
    }
    return (
      <PrintShell
        projectName={project.name}
        docType={docType}
        versionNumber={prdVersion.versionNumber}
      >
        <PrdPrintBody content={prdVersion.content as PrdContent} />
      </PrintShell>
    );
  }

  if (docType === 'tdd') {
    if (!tddVersion) {
      return (
        <PrintShell projectName={project.name} docType={docType}>
          <p className="text-sm text-muted">No accepted TDD yet.</p>
        </PrintShell>
      );
    }
    return (
      <PrintShell
        projectName={project.name}
        docType={docType}
        versionNumber={tddVersion.versionNumber}
      >
        <TddPrintBody content={tddVersion.content as TddContent} />
      </PrintShell>
    );
  }

  if (tasksLoading) {
    return (
      <PrintShell projectName={project.name} docType={docType}>
        <p className="text-sm text-muted">Loading…</p>
      </PrintShell>
    );
  }

  return (
    <PrintShell projectName={project.name} docType={docType}>
      {docType === 'tasks' ? (
        <TasksPrintBody tasks={tasks} />
      ) : (
        <TimelinePrintBody tasks={tasks} startDate={project.startDate} />
      )}
    </PrintShell>
  );
}
